import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { BobFinancialClient } from "@/api/bobFinancialClient";
import { registerOdooLoginHandler } from "@/api/odooLoginBridge";
import {
  connectOdooPortal,
  disconnectOdooPortal,
} from "@/api/odooStorage";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const OdooContext = createContext(null);

function partnerFromDashboard(me) {
  return String(me?.partner?.name || "");
}

export function OdooProvider({ children }) {
  const { customer, token } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [partnerName, setPartnerName] = useState("");
  const [linkError, setLinkError] = useState("");
  const [linked, setLinked] = useState(false);
  const refreshGeneration = useRef(0);
  const lastCustomerId = useRef(null);

  const clearState = useCallback(() => {
    setClient(null);
    setPartnerName("");
    setLinkError("");
    setLinked(false);
  }, []);

  const applyJwtClient = useCallback(async (sessionToken, generation) => {
    const next = new BobFinancialClient(sessionToken);
    const me = await next.getDashboardSummary();
    if (generation !== refreshGeneration.current) return;
    setClient(next);
    setPartnerName(partnerFromDashboard(me));
    setLinkError("");
    setLinked(true);
  }, []);

  /**
   * BOB JWT → website backend → Odoo (email/phone of the current BOB user).
   * Never reuse a previous user's cached Odoo portal API key.
   */
  const refreshClient = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    if (!customer || !token) {
      clearState();
      return;
    }

    try {
      await applyJwtClient(token, generation);
    } catch (jwtError) {
      if (generation !== refreshGeneration.current) return;
      clearState();
      setLinkError(
        jwtError instanceof Error
          ? jwtError.message
          : "Could not load your Customer Portal data.",
      );
    }
  }, [applyJwtClient, clearState, customer, token]);

  const connectPortal = useCallback(
    async (email, password) => {
      setLinkError("");
      const generation = refreshGeneration.current;
      try {
        const { client: portalClient } = await connectOdooPortal({
          email: String(email || "").trim(),
          password,
          device_name: "BOB website",
        });
        const me = await portalClient.getDashboardSummary();
        if (generation !== refreshGeneration.current) return false;
        setClient(portalClient);
        setPartnerName(partnerFromDashboard(me));
        setLinkError("");
        setLinked(true);
        return true;
      } catch (error) {
        if (generation !== refreshGeneration.current) return false;
        setLinkError(
          error instanceof Error
            ? error.message
            : "Could not open Customer Portal.",
        );
        setLinked(false);
        setClient(null);
        setPartnerName("");
        return false;
      }
    },
    [],
  );

  const disconnect = useCallback(async () => {
    refreshGeneration.current += 1;
    await disconnectOdooPortal();
    clearState();
  }, [clearState]);

  // After BOB login, optional portal password link — JWT path remains primary.
  useEffect(() => {
    registerOdooLoginHandler(async ({ email, password }) => {
      if (!email || !password) return;
      try {
        await connectPortal(email, password);
      } catch {
        /* BOB session still used via JWT */
      }
    });
    return () => registerOdooLoginHandler(null);
  }, [connectPortal]);

  // Clear financial session whenever the BOB customer identity changes.
  useEffect(() => {
    const customerId = customer?.id || null;
    if (lastCustomerId.current && lastCustomerId.current !== customerId) {
      refreshGeneration.current += 1;
      clearState();
      void disconnectOdooPortal();
    }
    lastCustomerId.current = customerId;
  }, [clearState, customer?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await refreshClient();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
      refreshGeneration.current += 1;
    };
  }, [customer?.id, token, refreshClient]);

  const value = useMemo(
    () => ({
      loading,
      linked,
      client,
      partnerName,
      linkError,
      connectPortal,
      refreshClient,
      disconnect,
    }),
    [client, connectPortal, disconnect, linkError, linked, loading, partnerName, refreshClient],
  );

  return <OdooContext.Provider value={value}>{children}</OdooContext.Provider>;
}

export function useOdoo() {
  const ctx = useContext(OdooContext);
  if (!ctx) throw new Error("useOdoo must be used inside OdooProvider");
  return ctx;
}
